import { useState } from "react";
import { Link } from "react-router-dom";
import { forgotPassword, resetPassword, apiError } from "@/lib/api";
import { AuthShell, AuthField, AuthErrorBanner, AuthSubmitButton } from "@/components/AuthLayout";
import { MailCheck, CheckCircle2 } from "lucide-react";

const CodeSentNote = ({ email }) => (
    <div className="flex items-start gap-3 border border-electric/40 bg-electric/10 px-3 py-2.5" data-testid="forgot-code-sent">
        <MailCheck className="w-4 h-4 text-electric mt-0.5 shrink-0" />
        <p className="font-mono text-xs text-white/80 leading-relaxed">
            We sent a 6-digit code to <span className="text-white font-bold">{email}</span>. Enter it below with your new password. The code expires in 15 minutes.
        </p>
    </div>
);

const SuccessNote = () => (
    <div className="text-center py-4" data-testid="forgot-success">
        <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto" />
        <p className="font-mono text-xs text-white/70 mt-4 leading-relaxed">
            Password updated. You can now log in with your new password.
        </p>
        <Link
            to="/login"
            className="inline-block mt-6 px-6 py-3 bg-blaze hover:bg-blazeHover text-white font-mono uppercase text-[11px] tracking-[0.25em] transition-colors"
            data-testid="forgot-go-login"
        >
            Log In →
        </Link>
    </div>
);

const EmailStep = ({ email, setEmail, error, busy, onSubmit }) => (
    <form onSubmit={onSubmit} className="space-y-4">
        <AuthField
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            testId="forgot-email-input"
        />
        <AuthErrorBanner error={error} testId="forgot-error" />
        <AuthSubmitButton busy={busy} testId="forgot-send-button">
            Send Code →
        </AuthSubmitButton>
    </form>
);

const ResetStep = ({ email, fields, error, busy, onSubmit, onResend }) => (
    <form onSubmit={onSubmit} className="space-y-4">
        <CodeSentNote email={email} />
        <AuthField
            label="6-Digit Code"
            required
            inputMode="numeric"
            minLength={6}
            maxLength={6}
            value={fields.code}
            onChange={fields.onCode}
            testId="forgot-code-input"
        />
        <AuthField
            label="New Password"
            type="password"
            required
            minLength={6}
            value={fields.newPassword}
            onChange={fields.onNewPassword}
            testId="forgot-new-password-input"
        />
        <AuthField
            label="Confirm New Password"
            type="password"
            required
            value={fields.confirm}
            onChange={fields.onConfirm}
            testId="forgot-confirm-input"
        />
        <AuthErrorBanner error={error} testId="forgot-error" />
        <AuthSubmitButton busy={busy} testId="forgot-reset-button">
            Reset Password →
        </AuthSubmitButton>
        <button
            type="button"
            onClick={onResend}
            disabled={busy}
            className="w-full font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-blaze transition-colors"
            data-testid="forgot-resend-button"
        >
            Resend code
        </button>
    </form>
);

function useForgotFlow() {
    const [step, setStep] = useState("email"); // email | reset | done
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const run = async (action) => {
        setError(null);
        setBusy(true);
        try {
            await action();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const sendCode = (e) => {
        e.preventDefault();
        run(async () => {
            await forgotPassword(email.trim());
            setStep("reset");
        });
    };

    const doReset = (e) => {
        e.preventDefault();
        if (newPassword !== confirm) {
            setError("Passwords do not match");
            return;
        }
        run(async () => {
            await resetPassword(email.trim(), code.trim(), newPassword);
            setStep("done");
        });
    };

    return {
        step,
        email,
        setEmail,
        error,
        busy,
        sendCode,
        doReset,
        fields: {
            code,
            onCode: (e) => setCode(e.target.value.replace(/\D/g, "")),
            newPassword,
            onNewPassword: (e) => setNewPassword(e.target.value),
            confirm,
            onConfirm: (e) => setConfirm(e.target.value),
        },
    };
}

export default function ForgotPassword() {
    const flow = useForgotFlow();

    return (
        <AuthShell
            pageTestId="forgot-page"
            title="Reset Password"
            subtitle={flow.step === "email" ? "We'll email you a code" : "Enter the code from your inbox"}
            footer={
                <>
                    Remembered it?{" "}
                    <Link to="/login" className="text-blaze hover:underline" data-testid="forgot-back-login">
                        Log in
                    </Link>
                </>
            }
        >
            {flow.step === "email" && (
                <EmailStep
                    email={flow.email}
                    setEmail={flow.setEmail}
                    error={flow.error}
                    busy={flow.busy}
                    onSubmit={flow.sendCode}
                />
            )}
            {flow.step === "reset" && (
                <ResetStep
                    email={flow.email.trim()}
                    fields={flow.fields}
                    error={flow.error}
                    busy={flow.busy}
                    onSubmit={flow.doReset}
                    onResend={flow.sendCode}
                />
            )}
            {flow.step === "done" && <SuccessNote />}
        </AuthShell>
    );
}
